import {
  Card,
  CardHeader,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  Box,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import "../../App.css";
import IosShareIcon from "@mui/icons-material/IosShare";
import {
  StyledCardTitle,
  StyledExport,
} from "../../StyledComponents/StyledComponents";
import { Search } from "@mui/icons-material";
import UsersTable from "./usersTable";
import SelectLabels from "../../components/menuItem";
import { AppContext } from "../../Context/AppContext";
import { useNavigate } from "react-router-dom";
import { planDetailsApi } from "../../api/adminApi";
import DateRangeSelector from "../../components/datePicker";
import { Helmet } from "react-helmet-async";
import { debounce } from "lodash";
import UserPlansDialog from "../planDetailsPage/userPlansDialog";
import { isEmailAllowedForMembers } from "../../utils/auth";

export default function UsersPage() {
  const { limit } = useContext(AppContext);
  const theme = useTheme();
  const navigate = useNavigate();
  const isSmallerScreen = useMediaQuery(theme.breakpoints.down("md"));
  const drawerWidth = 0;
  const [searchText, setSearchText] = useState("");
  const [isSubmit, setIsSubmit] = useState(false);
  const [Loader, setLoader] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [page, setPage] = useState(0);
  const [planData, setData] = useState([]);
  const [planCount, setCount] = useState(0);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedUserMobile, setSelectedUserMobile] = useState(null);
  const [loading, setLoading] = useState(false);
  const noDataFound  = planData.length === 0;
  
  const resultLimit = limit === "" ? 10 : limit;

  const serializedObject = sessionStorage?.getItem("userDetails");
  const userDetails = serializedObject ? JSON.parse(serializedObject) : null;
  const adminEmail = userDetails?.email || "";
  const isAllowed = isEmailAllowedForMembers(adminEmail);

  function formatDate(inputDateStr) {
    if (!inputDateStr) return "";
    const dateVal = inputDateStr.$d || inputDateStr;
    const dateObj = new Date(dateVal);
    if (!isNaN(dateObj.getTime())) {
      const month = String(dateObj.getMonth() + 1).padStart(2, "0");
      const day = String(dateObj.getDate()).padStart(2, "0");
      const year = String(dateObj.getFullYear());
      return `${month}-${day}-${year}`;
    }
    return "";
  }

  async function handleDownload() {
    setLoading(true);
    let isForSheet = "true";
    planDetailsApi(page * resultLimit, resultLimit, searchText, isForSheet).then(() => {
      setLoading(false);
    });
  }

  const debouncedAPICall = debounce(async (query) => {
    let isForSheet = "false";
    setIsLoading(true);
    await planDetailsApi(
      0,
      resultLimit,
      query,
      isForSheet,
      formatDate(startDate),
      formatDate(endDate)
    ).then((res) => {
      if (res?.statusCode == 404) {
        navigate("/");
      }
      setIsLoading(false);
      setData(Array.isArray(res?.message) ? res.message : []);
      setPage(0);
      setCount(Number(res?.count) || 0);
    });
  }, 0);

  const handleSearchChange = async (event) => {
    let { value } = event.target;
    if (value == "") {
      await debouncedAPICall(value);
    }
    setSearchText(value);
  };

  const handlePageChange = (params) => {
    setPage(params.page);
  };

  const handleStartDateChange = (date) => {
    setStartDate(date);
    setPage(0);
  };

  const handleSearchClick = async () => {
    await debouncedAPICall(searchText);
    setLoader(false);
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
    setPage(0);
  };

  const handleSearchSubmit = async (e) => {
    e.preventDefault();
    await debouncedAPICall(searchText);
    setIsSubmit(false);
  };

  useEffect(() => {
    if (!isAllowed) {
      setLoader(false);
      setIsLoading(false);
      return;
    }
    let isForSheet = "false";
    setIsLoading(true);

    planDetailsApi(
      page * resultLimit,
      resultLimit,
      searchText,
      isForSheet,
      formatDate(startDate),
      formatDate(endDate)
    ).then((res) => {
      if (res?.statusCode == 404) {
        navigate("/");
      }
      setData(Array.isArray(res?.message) ? res.message : []);
      setCount(Number(res?.count) || 0);
      setLoader(false);
      setIsLoading(false);
    });
  }, [page, limit, startDate, endDate, isAllowed]);

  if (!isAllowed) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", minHeight: "60vh", p: 4 }}>
        <Typography variant="h4" color="error" sx={{ fontWeight: "bold", mb: 2, fontFamily: "Open Sans" }}>
          Access Denied
        </Typography>
        <Typography variant="body1" sx={{ color: "#8E8D9A", fontFamily: "Open Sans" }}>
          You do not have permission to view the Members page.
        </Typography>
      </Box>
    );
  }

  if (Loader == true) {
    return (
      <div className="ayoub">
        <div className="loader"></div>
      </div>
    );
  } else {
    return (
      <>
        <Helmet>
          <title> Members </title>
        </Helmet>
        <Grid
          container
          sx={{
            p: "25px",
          }}
        >
          <Grid
            item
            sx={{
              backgroundColor: "#FAF2FF",
              width: isSmallerScreen ? "100%" : `calc(100% - ${drawerWidth}px)`,
              ml: { md: `${drawerWidth}px` },
            }}
          >
            <Grid container direction={"column"}>
              <Grid item>
                <Card>
                  <CardHeader
                    sx={{ display: "flex", justifyContent: "space-between" }}
                    title={
                      <Grid
                        display="flex"
                        alignItems={"center"}
                        justifyContent="space-between"
                      >
                        <StyledCardTitle> Registered Users List </StyledCardTitle>
                        
                        <StyledExport
                          variant="contained"
                          onClick={handleDownload}
                          disabled={noDataFound}
                        >
                          {loading ? (
                            <CircularProgress
                              size={25}
                              sx={{
                                color: "white", 
                              }}
                            />
                          ) : (
                            <IosShareIcon
                              sx={{
                                width: "19px",
                                height: "19px",
                                pb: "5px",
                              }}
                            />
                          )}
                          Export
                        </StyledExport>
                      </Grid>
                    }
                  />
                  <Divider />
                  <Card sx={{ height: "100%" }}>
                    <Grid
                      container
                      direction="row"
                      alignItems={"start"}
                      justifyContent={"space-evenly"}
                    >
                      <Grid item xl={1.8} lg={2.8} md={3.8} sm={5.9} xs={5.9}>
                        <CardHeader
                          sx={{ p: "12px" }}
                          title={
                            <SelectLabels
                              setPage={setPage}
                              textTitle={"Limit"}
                            />
                          }
                        />
                      </Grid>
                      {isSmallerScreen ? null : (
                        <Grid
                          item
                          alignSelf={"center"}
                          justifyContent="center"
                          xs={0.01}
                        >
                          <Divider
                            sx={{
                              height: "80px",
                            }}
                            orientation="vertical"
                          />
                        </Grid>
                      )}
                      <Grid item xl={4} lg={4.5} md={3.8} sm={5.9} xs={5.9}>
                        <CardHeader
                          sx={{ p: "12px" }}
                          title={
                            <form onSubmit={handleSearchSubmit}>
                              <TextField
                                id="outlined-basic"
                                variant="outlined"
                                placeholder="Search..."
                                value={searchText}
                                onChange={handleSearchChange}
                                size="small"
                                fullWidth
                                inputProps={{ maxLength: 100 }}
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <IconButton
                                        onClick={handleSearchClick}
                                        edge="end"
                                      >
                                        <Search />
                                      </IconButton>
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            </form>
                          }
                        />
                      </Grid>
                      {isSmallerScreen ? null : (
                        <Grid
                          item
                          alignSelf={"center"}
                          justifyContent="center"
                          xs={0.01}
                        >
                          <Divider
                            sx={{
                              height: "80px",
                            }}
                            orientation="vertical"
                          />
                        </Grid>
                      )}
                      <Grid item xl={4} lg={4} md={4} sm={6} xs={6}>
                        <CardHeader
                          sx={{ p: "12px" }}
                          title={
                            <DateRangeSelector
                              endDate={endDate}
                              setEndDate={setEndDate}
                              setStartDate={setStartDate}
                              startDate={startDate}
                              handleStartDateChange={handleStartDateChange}
                              handleEndDateChange={handleEndDateChange}
                            />
                          }
                        />
                      </Grid>
                    </Grid>
                  </Card>
                </Card>
              </Grid>
              <UsersTable
                handlePageChange={handlePageChange}
                planData={planData}
                planCount={Number(planCount) || 0}
                page={page}
                resultLimit={resultLimit}
                setPage={setPage}
                Loader={Loader}
                isLoading={isLoading}
                onRowClick={(row) => {
                  if (row.Mobile) {
                    setSelectedUserMobile(row.Mobile);
                    setIsDialogOpen(true);
                  }
                }}
              />
            </Grid>
          </Grid>
        </Grid>
        <UserPlansDialog
          open={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          userMobile={selectedUserMobile}
        />
      </>
    );
  }
}
